<template>
  <div class="products-page">
    <el-card shadow="never" class="table-card">
      <div slot="header" class="card-header">
        <span class="title-text">商品列表</span>
        <el-button type="primary" size="small" class="add-btn" @click="openEdit()">
          <i class="el-icon-plus" /> 添加商品
        </el-button>
      </div>

      <el-table
        v-loading="loading"
        :data="list"
        class="products-table"
        :header-cell-style="headerStyle"
      >
        <el-table-column label="编号" width="80">
          <template slot-scope="{ row }">{{ formatProductCode(row.id) }}</template>
        </el-table-column>
        <el-table-column prop="name" label="商品名称" min-width="160" show-overflow-tooltip />
        <el-table-column prop="platform" label="平台" width="110" />
        <el-table-column label="分类" width="110">
          <template slot-scope="{ row }">{{ row.categoryName || '-' }}</template>
        </el-table-column>
        <el-table-column label="价格" width="100">
          <template slot-scope="{ row }">
            <span class="price">¥{{ formatPrice(row.price) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="评分" width="90">
          <template slot-scope="{ row }">
            <span class="rating-cell">
              <span class="rating-dot" :style="{ background: row.colorTag || '#94a3b8' }" />
              {{ row.rating != null ? row.rating : '-' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="佣金%" width="80">
          <template slot-scope="{ row }">{{ row.commissionRate != null ? `${row.commissionRate}%` : '-' }}</template>
        </el-table-column>
        <el-table-column label="销量" width="80">
          <template slot-scope="{ row }">{{ row.salesCount != null ? row.salesCount : 0 }}</template>
        </el-table-column>
        <el-table-column label="状态" width="90">
          <template slot-scope="{ row }">
            <span :class="['status-badge', row.status === 1 ? 'enabled' : 'disabled']">
              {{ labelOf(COMMON_STATUS, row.status) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="160" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" class="action-view" @click="openDetail(row)">查看</el-button>
            <el-button type="text" size="small" class="action-edit" @click="openEdit(row)">编辑</el-button>
            <el-button type="text" size="small" class="action-delete" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 商品详情 -->
    <el-dialog
      :visible.sync="detailVisible"
      width="560px"
      custom-class="product-detail-dialog"
      @closed="detail = null"
    >
      <div v-if="detail" slot="title" class="dialog-title">
        <span class="dialog-icon detail"><i class="el-icon-box" /></span>
        <span>商品详情</span>
      </div>
      <div v-if="detail" class="detail-body">
        <div class="detail-field full">
          <span class="field-label">商品名称</span>
          <div class="field-box">{{ detail.name }}</div>
        </div>
        <div class="detail-row">
          <div class="detail-field">
            <span class="field-label">平台</span>
            <div class="field-box">{{ detail.platform }}</div>
          </div>
          <div class="detail-field">
            <span class="field-label">分类</span>
            <div class="field-box">{{ detail.categoryName || '-' }}</div>
          </div>
        </div>
        <div class="detail-row">
          <div class="detail-field">
            <span class="field-label">价格</span>
            <div class="field-box highlight-price">¥{{ formatPrice(detail.price) }}</div>
          </div>
          <div class="detail-field">
            <span class="field-label">销量</span>
            <div class="field-box">{{ detail.salesCount != null ? detail.salesCount : 0 }}</div>
          </div>
        </div>
        <div class="detail-row">
          <div class="detail-field">
            <span class="field-label">商品评分</span>
            <div class="field-box rating-box">
              <span class="highlight-rating">{{ detail.rating != null ? detail.rating : '-' }}</span>
              <span v-if="detail.colorTag" class="rating-dot" :style="{ background: detail.colorTag }" />
            </div>
          </div>
          <div class="detail-field">
            <span class="field-label">代理佣金</span>
            <div class="field-box highlight-commission">
              {{ detail.commissionRate != null ? `${detail.commissionRate}%` : '-' }}
            </div>
          </div>
        </div>
        <div class="detail-field full">
          <span class="field-label">商品详情</span>
          <div v-if="detailDescriptionHtml" class="field-box detail-rich" v-html="detailDescriptionHtml" />
          <div v-else class="field-box">—</div>
        </div>
      </div>
      <div slot="footer">
        <el-button @click="detailVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 新增/编辑 -->
    <el-dialog
      :visible.sync="editVisible"
      width="680px"
      top="5vh"
      custom-class="product-edit-dialog"
      @closed="resetForm"
    >
      <div slot="title" class="dialog-title">
        <span class="dialog-icon edit"><i class="el-icon-box" /></span>
        <span>{{ form.id ? '编辑商品' : '添加商品' }}</span>
      </div>
      <el-form ref="form" :model="form" :rules="rules" label-position="top" class="edit-form">
        <div class="form-row">
          <el-form-item label="商品名称" prop="name" class="form-col">
            <el-input v-model="form.name" placeholder="请输入商品名称" />
          </el-form-item>
          <el-form-item label="商品平台" prop="platform" class="form-col">
            <el-select v-model="form.platform" placeholder="请选择平台" style="width: 100%">
              <el-option v-for="p in platformOptions" :key="p" :label="p" :value="p" />
            </el-select>
          </el-form-item>
        </div>
        <div class="form-row">
          <el-form-item label="商品分类" prop="categoryId" class="form-col">
            <el-select v-model="form.categoryId" placeholder="请选择分类" style="width: 100%">
              <el-option v-for="c in categories" :key="c.id" :label="c.name" :value="c.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="商品价格 (元)" prop="price" class="form-col">
            <el-input v-model.number="form.price" type="number" placeholder="0" />
          </el-form-item>
        </div>
        <div class="form-row">
          <el-form-item label="计价单位" class="form-col">
            <el-input v-model="form.unit" placeholder="如 篇、次、天" />
          </el-form-item>
          <el-form-item label="起订量" class="form-col">
            <el-input v-model.number="form.minOrderQty" type="number" placeholder="1" />
          </el-form-item>
        </div>
        <div class="form-row">
          <el-form-item label="商品评分" class="form-col">
            <el-input v-model.number="form.rating" type="number" step="0.1" placeholder="如 4.8" />
          </el-form-item>
          <el-form-item label="颜色标记" class="form-col">
            <div class="color-picker-row">
              <input v-model="form.colorTag" type="color" class="color-input">
              <el-input v-model="form.colorTag" placeholder="#ef4444" />
            </div>
          </el-form-item>
        </div>
        <div class="form-row">
          <el-form-item label="代理佣金 (%)" class="form-col">
            <el-input v-model.number="form.commissionRate" type="number" placeholder="15" />
          </el-form-item>
          <el-form-item label="销量" class="form-col">
            <el-input v-model.number="form.salesCount" type="number" placeholder="0" />
          </el-form-item>
        </div>
        <el-form-item label="状态">
          <el-select v-model="form.status" style="width: 200px">
            <el-option v-for="opt in statusOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="商品详情描述">
          <rich-text-editor
            v-if="editVisible"
            v-model="form.description"
            biz-type="cms"
            height="280px"
            placeholder="请输入商品详情描述，支持图文排版"
          />
        </el-form-item>
        <el-form-item label="购买须知">
          <el-input v-model="form.purchaseNotes" type="textarea" :rows="3" placeholder="请输入购买须知" />
        </el-form-item>
        <div class="faq-section">
          <div class="faq-header">
            <span class="faq-title">常见问题 (FAQ)</span>
            <el-button type="primary" size="mini" plain class="faq-add-btn" @click="addFaq">
              <i class="el-icon-plus" /> 添加常见问题
            </el-button>
          </div>
          <div v-for="(item, index) in form.faqList" :key="index" class="faq-item">
            <div class="faq-item-head">
              <span class="faq-index">Q{{ index + 1 }}</span>
              <el-button type="text" size="mini" class="faq-delete" @click="removeFaq(index)">删除</el-button>
            </div>
            <div class="faq-row">
              <el-input v-model="item.question" type="textarea" :rows="2" placeholder="问题" />
              <el-input v-model="item.answer" type="textarea" :rows="2" placeholder="答案" />
            </div>
          </div>
        </div>
      </el-form>
      <div slot="footer">
        <el-button @click="editVisible = false">取消</el-button>
        <el-button type="primary" class="confirm-btn" :loading="submitting" @click="submit">
          {{ form.id ? '确认保存' : '确认添加' }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getTrafficProducts, getTrafficCategories, saveTrafficProduct, deleteTrafficProduct } from '@/api/admin'
import { COMMON_STATUS, labelOf, optionsOf } from '@/utils/adminConstants'
import RichTextEditor from '@/components/admin/RichTextEditor.vue'

const PLATFORM_OPTIONS = ['小红书', '抖音', 'Bilibili', '快手', '微信视频号']

function emptyForm() {
  return {
    id: null,
    name: '',
    platform: '',
    categoryId: null,
    price: null,
    unit: '篇',
    rating: null,
    commissionRate: null,
    salesCount: 0,
    colorTag: '#ef4444',
    minOrderQty: 1,
    description: '',
    purchaseNotes: '',
    faqList: [],
    status: 1
  }
}

export default {
  name: 'TrafficProducts',
  components: { RichTextEditor },
  data() {
    return {
      COMMON_STATUS,
      loading: false,
      list: [],
      categories: [],
      platformOptions: PLATFORM_OPTIONS,
      detailVisible: false,
      detail: null,
      editVisible: false,
      submitting: false,
      form: emptyForm(),
      rules: {
        name: [{ required: true, message: '请输入商品名称', trigger: 'blur' }],
        platform: [{ required: true, message: '请选择平台', trigger: 'change' }],
        categoryId: [{ required: true, message: '请选择分类', trigger: 'change' }],
        price: [{ required: true, message: '请输入价格', trigger: 'blur' }]
      }
    }
  },
  computed: {
    statusOptions() {
      return optionsOf(COMMON_STATUS)
    },
    detailDescriptionHtml() {
      const raw = this.detail?.description
      if (!raw) return ''
      if (/<[a-z][\s\S]*>/i.test(raw)) return raw
      return raw.replace(/\n/g, '<br/>')
    }
  },
  created() {
    this.loadCategories()
    this.loadList()
  },
  methods: {
    labelOf,
    headerStyle() {
      return { background: '#fafafa', color: '#606266', fontWeight: '600' }
    },
    formatProductCode(id) {
      return id != null ? `P${String(id).padStart(3, '0')}` : '-'
    },
    formatPrice(price) {
      if (price == null) return '0'
      const n = Number(price)
      return Number.isInteger(n) ? n : n.toFixed(2)
    },
    async loadCategories() {
      try {
        this.categories = await getTrafficCategories() || []
      } catch (e) {
        this.categories = []
      }
    },
    async loadList() {
      this.loading = true
      try {
        this.list = await getTrafficProducts() || []
      } catch (e) {
        this.list = []
      } finally {
        this.loading = false
      }
    },
    openDetail(row) {
      this.detail = { ...row }
      this.detailVisible = true
    },
    parseFaqList(faqJson) {
      if (!faqJson) return []
      try {
        const parsed = JSON.parse(faqJson)
        return Array.isArray(parsed) ? parsed.map(item => ({
          question: item.question || '',
          answer: item.answer || ''
        })) : []
      } catch (e) {
        return []
      }
    },
    openEdit(row) {
      if (row) {
        this.form = {
          id: row.id,
          name: row.name,
          platform: row.platform,
          categoryId: row.categoryId,
          price: row.price != null ? Number(row.price) : null,
          unit: row.unit || '篇',
          rating: row.rating != null ? Number(row.rating) : null,
          commissionRate: row.commissionRate,
          salesCount: row.salesCount != null ? row.salesCount : 0,
          colorTag: row.colorTag || '#ef4444',
          minOrderQty: row.minOrderQty != null ? row.minOrderQty : 1,
          description: row.description || '',
          purchaseNotes: row.purchaseNotes || '',
          faqList: this.parseFaqList(row.faqJson),
          status: row.status != null ? row.status : 1
        }
      } else {
        this.form = emptyForm()
      }
      this.editVisible = true
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.clearValidate()
      })
    },
    resetForm() {
      this.form = emptyForm()
    },
    addFaq() {
      this.form.faqList.push({ question: '', answer: '' })
    },
    removeFaq(index) {
      this.form.faqList.splice(index, 1)
    },
    buildPayload() {
      const faqList = (this.form.faqList || []).filter(item => item.question || item.answer)
      return {
        id: this.form.id,
        name: this.form.name,
        platform: this.form.platform,
        categoryId: this.form.categoryId,
        price: this.form.price,
        unit: this.form.unit,
        rating: this.form.rating,
        commissionRate: this.form.commissionRate,
        salesCount: this.form.salesCount,
        colorTag: this.form.colorTag,
        minOrderQty: this.form.minOrderQty,
        description: this.form.description,
        purchaseNotes: this.form.purchaseNotes,
        faqJson: JSON.stringify(faqList),
        status: this.form.status
      }
    },
    submit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return
        this.submitting = true
        try {
          await saveTrafficProduct(this.buildPayload())
          this.$message.success(this.form.id ? '保存成功' : '添加成功')
          this.editVisible = false
          this.loadList()
        } catch (e) {
        } finally {
          this.submitting = false
        }
      })
    },
    handleDelete(row) {
      this.$confirm(`确定删除商品「${row.name}」？`, '删除确认', { type: 'warning' })
        .then(async () => {
          await deleteTrafficProduct(row.id)
          this.$message.success('已删除')
          this.loadList()
        }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.products-page {
  padding: 0;
}
.table-card {
  border: none;
  border-radius: 12px;
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.title-text {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}
.add-btn {
  background: #f97316;
  border-color: #f97316;
}
.add-btn:hover,
.add-btn:focus {
  background: #ea580c;
  border-color: #ea580c;
}
.price {
  color: #16a34a;
  font-weight: 500;
}
.rating-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.rating-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.status-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
}
.status-badge.enabled {
  background: #dcfce7;
  color: #16a34a;
}
.status-badge.disabled {
  background: #f3f4f6;
  color: #9ca3af;
}
.action-view {
  color: #3b82f6;
}
.action-edit {
  color: #f97316;
}
.action-delete {
  color: #ef4444;
}
.dialog-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
}
.dialog-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  font-size: 14px;
}
.dialog-icon.detail {
  background: #fef3c7;
  color: #d97706;
}
.dialog-icon.edit {
  background: #ffedd5;
  color: #ea580c;
}
.detail-body {
  padding: 0 4px;
}
.detail-row {
  display: flex;
  gap: 16px;
}
.detail-field {
  flex: 1;
  margin-bottom: 16px;
}
.detail-field.full {
  width: 100%;
}
.field-label {
  display: block;
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 6px;
}
.field-box {
  padding: 10px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
  color: #374151;
  font-size: 14px;
}
.highlight-price,
.highlight-rating {
  color: #f97316;
  font-weight: 600;
}
.highlight-commission {
  color: #16a34a;
  font-weight: 600;
}
.rating-box {
  display: flex;
  align-items: center;
  gap: 8px;
}
.detail-rich {
  line-height: 1.6;
  word-break: break-word;
}
.detail-rich :deep(img) {
  max-width: 100%;
  height: auto;
}
.edit-form {
  max-height: 65vh;
  overflow-y: auto;
  padding-right: 8px;
}
.form-row {
  display: flex;
  gap: 16px;
}
.form-col {
  flex: 1;
}
.color-picker-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.color-input {
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  flex-shrink: 0;
}
.faq-section {
  margin-top: 8px;
}
.faq-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.faq-title {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}
.faq-add-btn {
  color: #f97316;
  border-color: #fdba74;
}
.faq-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  background: #fafafa;
}
.faq-item-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.faq-index {
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
}
.faq-delete {
  color: #ef4444;
}
.faq-row {
  display: flex;
  gap: 12px;
}
.confirm-btn {
  background: #f97316;
  border-color: #f97316;
}
.confirm-btn:hover,
.confirm-btn:focus {
  background: #ea580c;
  border-color: #ea580c;
}
</style>

<style>
.product-detail-dialog .el-dialog__header,
.product-edit-dialog .el-dialog__header {
  padding: 20px 20px 10px;
  border-bottom: none;
}
.product-detail-dialog .el-dialog__body,
.product-edit-dialog .el-dialog__body {
  padding: 10px 20px 20px;
}
.product-edit-dialog .el-form-item__label {
  padding-bottom: 4px;
  line-height: 1.4;
  color: #374151;
}
</style>
